import type { Region } from "./types";

export const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY ?? "";

/**
 * MapTiler "Customize" lets you fork a base style (e.g. dataviz-dark) and
 * recolour layers in their dashboard. Saving produces a unique map ID like
 * `01HXYZ...`. Set VITE_MAPTILER_MAP_ID to that ID to use it as the default
 * basemap. Falls back to "dataviz-dark" if not set.
 */
export const MAPTILER_MAP_ID =
  import.meta.env.VITE_MAPTILER_MAP_ID ?? "dataviz-dark";

const maptilerStyleUrl = (mapId: string): string =>
  `https://api.maptiler.com/maps/${mapId}/style.json?key=${MAPTILER_KEY}`;

/**
 * Embedded MapTiler key used as a dev fallback when VITE_MAPTILER_KEY is
 * unset. Lets new contributors render the real basemaps without having
 * to register for MapTiler first. Production builds should still set
 * VITE_MAPTILER_KEY so requests route through the project's own quota.
 */
const MAPTILER_KEY_FALLBACK = "gK95XIzK1zMFfWbV7Rdj";
const MAPTILER_KEY_RESOLVED = MAPTILER_KEY || MAPTILER_KEY_FALLBACK;

/**
 * Map tile style URL — the production default for the spot-detail map.
 * `VITE_MAPTILER_MAP_ID` (committed to `.env`) currently resolves to
 * MapTiler's `satellite-v4` style. Falls back to the embedded dev key
 * so local builds see the same satellite imagery as prod even without
 * a per-developer `.env.local`.
 */
export const MAP_STYLE = `https://api.maptiler.com/maps/${MAPTILER_MAP_ID}/style.json?key=${MAPTILER_KEY_RESOLVED}`;

/**
 * Style used by the region/spot list maps — MapTiler "Midnight": a dark
 * basemap with near-black land and deep-navy water that matches the
 * app's midnight surface palette.
 */
const LIST_MAP_ID = "019e2577-fa6b-7ae5-a0d0-ec594294e280";
export const LIST_MAP_STYLE = `https://api.maptiler.com/maps/${LIST_MAP_ID}/style.json?key=${MAPTILER_KEY_RESOLVED}`;

/**
 * Free-tier map styles for the dev/tuning page. CARTO basemaps don't need a
 * key. MapTiler entries are appended only when VITE_MAPTILER_KEY is set.
 */
export const MAP_STYLE_OPTIONS: Array<{ id: string; label: string; url: string }> = [
  {
    id: "carto-dark",
    label: "CARTO Dark Matter",
    url: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
  },
  {
    id: "carto-dark-no-labels",
    label: "CARTO Dark (no labels)",
    url: "https://basemaps.cartocdn.com/gl/dark-matter-nolabels-gl-style/style.json",
  },
  {
    id: "carto-positron",
    label: "CARTO Positron (light)",
    url: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
  },
  {
    id: "carto-positron-no-labels",
    label: "CARTO Positron (no labels)",
    url: "https://basemaps.cartocdn.com/gl/positron-nolabels-gl-style/style.json",
  },
  {
    id: "carto-voyager",
    label: "CARTO Voyager",
    url: "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json",
  },
  ...(MAPTILER_KEY
    ? [
        // The user's custom-styled map (from MapTiler Customize) — listed first
        // so it's the obvious choice when set. Skipped if it's the same as the
        // built-in "dataviz-dark" id (no point listing the duplicate).
        ...(MAPTILER_MAP_ID && MAPTILER_MAP_ID !== "dataviz-dark"
          ? [
              {
                id: "maptiler-custom",
                label: `MapTiler custom (${MAPTILER_MAP_ID})`,
                url: maptilerStyleUrl(MAPTILER_MAP_ID),
              },
            ]
          : []),
        {
          id: "maptiler-dataviz-dark",
          label: "MapTiler dataviz-dark",
          url: maptilerStyleUrl("dataviz-dark"),
        },
        {
          id: "maptiler-dataviz",
          label: "MapTiler dataviz (light)",
          url: maptilerStyleUrl("dataviz"),
        },
        {
          id: "maptiler-streets-dark",
          label: "MapTiler streets-dark",
          url: maptilerStyleUrl("streets-v2-dark"),
        },
      ]
    : []),
];

/**
 * Bounding box that fits all of Sweden with a small buffer.
 *
 * The southern edge extends well past Sweden (down to ~50° N — central
 * Germany) so the regions overview's initial camera isn't clipped by
 * the camera-pan limit. In practice the user can still pan into the
 * Baltic Sea / Denmark / North Germany area; that's harmless for an
 * interactive overview map.
 */
export const SWEDEN_BOUNDS: [[number, number], [number, number]] = [
  [9.5, 50.0],   // SW [lon, lat]
  [26.0, 70.5],  // NE [lon, lat]
];

/** Initial viewport centred on Sweden. */
export const SWEDEN_INITIAL_VIEW = {
  longitude: 16.5,
  latitude: 62.5,
  zoom: 4.2,
};

/** Drawer peek height on mobile — mirrors --drawer-peek in
 *  MapDrawer.module.css. fitBounds callers add this as extra bottom
 *  padding so map content stays above the drawer overhang. */
export const DRAWER_PEEK_PX = 200;

/** Return fitBounds padding that accounts for the drawer overhang on
 *  single-column (mobile) layouts. At xl the drawer flattens into a
 *  column and no longer covers the map, so symmetric padding applies.
 *
 *  Apply via `map.setPadding(getMapFitPadding())` before fitBounds —
 *  maplibre's fitBounds padding option computes zoom from the padding
 *  but does not shift the camera for asymmetric padding. setPadding
 *  persistently biases the camera so the bounds end up inside the
 *  un-occluded portion of the map. */
export function getMapFitPadding(
  base = 32,
): { top: number; bottom: number; left: number; right: number } {
  const isSingleColumn =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 799px)").matches;
  return {
    top: base,
    bottom: isSingleColumn ? base + DRAWER_PEEK_PX : base,
    left: base,
    right: base,
  };
}

/** Approximate centroids for the 8 regions. Used as region marker positions. */
export const REGION_CENTROIDS: Record<Region, [number, number]> = {
  stockholm:       [18.07, 59.33],
  "south-sweden":  [13.55, 55.95],
  "west-sweden":   [12.00, 57.70],
  "east-sweden":   [15.60, 58.00],
  "central-sweden":[15.30, 59.80],
  gotland:         [18.49, 57.47],
  oland:           [16.63, 56.74],
  "north-sweden":  [17.50, 64.50],
};

/**
 * Hex colors for score levels, matching the --wind-direction-* tokens.
 * Used in contexts where CSS variables are unavailable (canvas, GeoJSON paint).
 */
export const SCORE_HEX = {
  prime:    "#00ff55",
  good:     "#14a5ff",
  marginal: "#ffff00",
  unlikely: "#ff8800",
} as const;

/**
 * Wind speed → hex color for canvas particle rendering.
 * Mirrors the knots token scale at a coarser resolution.
 */
export const PARTICLE_SPEED_STOPS: Array<[knots: number, hex: string]> = [
  [0,  "#3366aa"],
  [4,  "#5599cc"],
  [8,  "#00aaff"],
  [14, "#00ff88"],
  [20, "#ffcc00"],
  [28, "#ff6600"],
  [40, "#ff2200"],
];
